import { LocationVarKey } from "@swivl-mono/types";
import { useMemo } from "react";

import type { PageKey, RouterWorksheetData } from "../Components/Worksheets/Types/Worksheet.Interface";
import LocationVarsModel from "../Models/Variables/LocationVars.Model";
import { checkCondition } from "./WorksheetRow.Hooks";

/**
 * Counts how many keys from a PageKey array are customized and visible.
 */
function countVisibleCustomizedKeys(
  pageKeys: PageKey[],
  scopeVars: Partial<Record<LocationVarKey, unknown>>,
  locationProps: { groupId?: number; locationId?: number },
  locationVarProps: { orgVariables: any; groupVariables: any; locationVariables: any; isLoaded: boolean }
): number {
  let count = 0;
  for (const pageKey of pageKeys) {
    if (!pageKey) continue;
    const key = typeof pageKey === "string" ? pageKey : pageKey.key;
    const hideIf = typeof pageKey === "string" ? undefined : pageKey.hideIf;

    if (hideIf && checkCondition(hideIf, locationProps, locationVarProps)) {
      continue;
    }
    if (key in scopeVars) {
      count++;
    }
  }
  return count;
}

/**
 * Computes per-section customization counts for worksheet sidebar items.
 * Returns a map from each item's `to` value to the number of its keys
 * that have been customized at the current scope (org/group/location).
 * Respects hideIf conditions — hidden children and fields are excluded.
 */
export function useWorksheetCustomizationCounts(items: RouterWorksheetData[], groupId?: number, locationId?: number): Record<string, number> {
  const { orgVariables, groupVariables, locationVariables, isLoaded } = LocationVarsModel.useState();

  return useMemo(() => {
    // Determine which variable map to check based on the current scope
    let scopeVars: Partial<Record<LocationVarKey, unknown>> = {};
    if (locationId && locationVariables[locationId]) {
      scopeVars = locationVariables[locationId];
    } else if (groupId && groupVariables[groupId]) {
      scopeVars = groupVariables[groupId];
    } else {
      scopeVars = orgVariables;
    }

    const locationProps = { groupId, locationId };
    const locationVarProps = { orgVariables, groupVariables, locationVariables, isLoaded };

    const counts: Record<string, number> = {};

    for (const item of items) {
      if (item.children && item.children.length > 0) {
        // Folder: compute per-child counts, folder total = sum of visible children
        let folderTotal = 0;
        for (const child of item.children) {
          // Skip hidden children
          if (child.hideIf && checkCondition(child.hideIf, locationProps, locationVarProps)) {
            continue;
          }
          if (child.keys) {
            const childCount = countVisibleCustomizedKeys(child.keys, scopeVars, locationProps, locationVarProps);
            if (childCount > 0) {
              counts[`${item.to}/${child.to}`] = childCount;
              folderTotal += childCount;
            }
          }
        }
        if (folderTotal > 0) {
          counts[item.to] = folderTotal;
        }
      } else if (item.keys) {
        const count = countVisibleCustomizedKeys(item.keys, scopeVars, locationProps, locationVarProps);
        if (count > 0) {
          counts[item.to] = count;
        }
      }
    }

    return counts;
  }, [items, groupId, locationId, orgVariables, groupVariables, locationVariables, isLoaded]);
}
